import api from '@/api/api';

const DOMAIN = '/sandbox';

const getStatus = async (): Promise<Sandbox.Status> => {
	const { data } = await api.get(`${DOMAIN}/status`);
	return data;
};

const getCatalog = async (): Promise<Sandbox.Catalog> => {
	const { data } = await api.get(`${DOMAIN}/catalog`);
	return data;
};

const listScenarios = async (): Promise<Sandbox.Scenario[]> => {
	const { data } = await api.get(`${DOMAIN}/scenarios`);
	return data;
};

const getScenario = async (id: string): Promise<Sandbox.ScenarioDetail> => {
	const { data } = await api.get(`${DOMAIN}/scenarios/${id}`);
	return data;
};

const createScenario = async (
	body: Sandbox.CreateScenario,
): Promise<Sandbox.Scenario> => {
	const { data } = await api.post(`${DOMAIN}/scenarios`, body);
	return data;
};

const deleteScenario = async (id: string): Promise<Sandbox.PurgeReport> => {
	const { data } = await api.delete(`${DOMAIN}/scenarios/${id}`);
	return data;
};

const resetScenario = async (id: string): Promise<Sandbox.PurgeReport> => {
	const { data } = await api.post(`${DOMAIN}/scenarios/${id}/reset`);
	return data;
};

const createUser = async (
	scenarioId: string,
	body: Sandbox.CreateUser,
): Promise<Sandbox.CreatedUser> => {
	const { data } = await api.post(
		`${DOMAIN}/scenarios/${scenarioId}/users`,
		body,
	);
	return data;
};

const createProfessional = async (
	scenarioId: string,
	body: Sandbox.CreateProfessional,
): Promise<Sandbox.CreatedProfessional> => {
	const { data } = await api.post(
		`${DOMAIN}/scenarios/${scenarioId}/professionals`,
		body,
	);
	return data;
};

const activateSubscription = async (
	scenarioId: string,
	body: Sandbox.ActivateSubscription,
): Promise<{ id: string }> => {
	const { data } = await api.post(
		`${DOMAIN}/scenarios/${scenarioId}/subscriptions`,
		body,
	);
	return data;
};

const cancelSubscription = async (
	scenarioId: string,
	subscriptionId: string,
): Promise<{ scheduled: boolean }> => {
	const { data } = await api.delete(
		`${DOMAIN}/scenarios/${scenarioId}/subscriptions/${subscriptionId}`,
	);
	return data;
};

const scheduleSession = async (
	scenarioId: string,
	body: Sandbox.ScheduleSession,
): Promise<Sandbox.ScenarioSession> => {
	const { data } = await api.post(
		`${DOMAIN}/scenarios/${scenarioId}/sessions`,
		body,
	);
	return data;
};

const paySession = async (
	scenarioId: string,
	sessionId: string,
	mode: 'MANUAL' | 'BENEFIT',
): Promise<Sandbox.ScenarioSession> => {
	const { data } = await api.post(
		`${DOMAIN}/scenarios/${scenarioId}/sessions/${sessionId}/pay`,
		{ mode },
	);
	return data;
};

const startSession = async (
	scenarioId: string,
	sessionId: string,
	timeTravel = true,
): Promise<Sandbox.ScenarioSession> => {
	const { data } = await api.post(
		`${DOMAIN}/scenarios/${scenarioId}/sessions/${sessionId}/start`,
		{ time_travel: timeTravel },
	);
	return data;
};

const endSession = async (
	scenarioId: string,
	sessionId: string,
	timeTravel = true,
): Promise<Sandbox.ScenarioSession> => {
	const { data } = await api.post(
		`${DOMAIN}/scenarios/${scenarioId}/sessions/${sessionId}/end`,
		{ time_travel: timeTravel },
	);
	return data;
};

const cancelSession = async (
	scenarioId: string,
	sessionId: string,
	by: Sandbox.CancelActor,
): Promise<Sandbox.ScenarioSession> => {
	const { data } = await api.post(
		`${DOMAIN}/scenarios/${scenarioId}/sessions/${sessionId}/cancel`,
		{ by },
	);
	return data;
};

const submitMatch = async (
	scenarioId: string,
	body: Sandbox.SubmitMatch,
): Promise<Match.Result> => {
	const { data } = await api.post(
		`${DOMAIN}/scenarios/${scenarioId}/match`,
		body,
	);
	return data;
};

const createCompany = async (
	scenarioId: string,
	body: Sandbox.CreateCompany,
): Promise<Sandbox.CreateCompanyResult> => {
	const { data } = await api.post(
		`${DOMAIN}/scenarios/${scenarioId}/companies`,
		body,
	);
	return data;
};

const runJourney = async (
	scenarioId: string,
	body: Sandbox.RunJourney,
): Promise<Sandbox.JourneyResult> => {
	const { data } = await api.post(
		`${DOMAIN}/scenarios/${scenarioId}/journey`,
		body,
	);
	return data;
};

const impersonate = async (
	scenarioId: string,
	body: { kind: Sandbox.ImpersonationKind; id: string },
): Promise<Sandbox.Impersonation> => {
	const { data } = await api.post(
		`${DOMAIN}/scenarios/${scenarioId}/impersonate`,
		body,
	);
	return data;
};

const SandboxService = {
	getStatus,
	getCatalog,
	listScenarios,
	getScenario,
	createScenario,
	deleteScenario,
	resetScenario,
	createUser,
	createProfessional,
	activateSubscription,
	cancelSubscription,
	scheduleSession,
	paySession,
	startSession,
	endSession,
	cancelSession,
	submitMatch,
	createCompany,
	runJourney,
	impersonate,
};

export { SandboxService };
